import { faEllipsisV, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { Table } from "../ui/Table";
import {
  createColumnHelper,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import * as Dialog from "@radix-ui/react-dialog";
import * as MenuBar from "@radix-ui/react-menubar";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  editRoute,
  getPrint,
  getUserRoutes,
  removeRoute,
} from "../../services/api";
import {
  setCurrentRoute,
  setMapCenter,
  setUserRoutesData,
} from "../../store/reducers/listingReducer";
import { baseQueryAPI } from "../../services/baseQuery";
import { Spinner } from "../ui/Spinner";
import { ReactComponent as CloseIcon } from "../../assets/svg/x-close.svg";
import { debounce } from "lodash";

export const RoutesPage = ({ isTeamRoutes }: { isTeamRoutes?: boolean }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const columnHelper = createColumnHelper<any>();
  const controller = useRef<AbortController | null>(null);

  const [printLoading, setPrintLoading] = useState(false);
  const [printingDoc, setPrintingDoc] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const logged = useSelector((state: any) => state.listingReducer.logged);
  const userRoutes = useSelector(
    (state: any) => state.listingReducer.userRoutes
  );

  const { data: routesData, refetch } = baseQueryAPI.useRoutesQuery("routes", {
    skip: !logged || isTeamRoutes,
  });

  const { data: teamRoutesData } = baseQueryAPI.useTeamRoutesQuery(
    "teamRoutes",
    {
      skip: !logged || !isTeamRoutes,
    }
  );

  useEffect(() => {
    if (routesData) {
      dispatch(setUserRoutesData(routesData.data));
    }

    if (teamRoutesData) {
      dispatch(setUserRoutesData(teamRoutesData.data));
    }
  }, [routesData, teamRoutesData]);

  function handleRouteView(currentRoute: any) {
    //let currentRoute = userRoutes.find((item: any) => item.public_id !== publicId);
    dispatch(setCurrentRoute(currentRoute));
    const path = JSON.parse(currentRoute.path);
    if (path) {
      dispatch(
        setMapCenter({
          lat: path[0].lat,
          lng: path[0].lng,
          search: true,
        })
      );
    }
    /*navigate(config.path + "route/" + publicId);
            dispatch(setRemindersShow(false));*/
  }

  function removeItemByPublicId(publicId: string) {
    // Use Array.prototype.filter to create a new array without the target item
    return userRoutes.filter((item: any) => item.public_id !== publicId);
  }

  async function PrintRouteDocs(publicId: string) {
    try {
      setPrintLoading(true);
      setPrintingDoc(publicId);
      controller.current = new AbortController();
      const signal = controller.current.signal;
      let result = await getPrint(publicId, signal);
      if (result?.headers["content-type"] === "application/pdf") {
        let pdfBlob = await result?.data;
        let url = URL.createObjectURL(pdfBlob);
        setPdfUrl(url);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setPrintLoading(false);
    }
  }

  async function handleDelete(publicId: string) {
    let params: any = {};
    params["public_id"] = publicId;
    let result = await removeRoute(params);
    if (!result?.data.success) {
      return;
    }
    let newRoutes = removeItemByPublicId(publicId);
    dispatch(setUserRoutesData(newRoutes));
  }

  const debouncedApiCall = debounce(async (row: any) => {
    try {
      await editRoute(row);
      refetch();
    } catch (error) {
      console.error(error);
    }
  }, 3000);

  const columns = useMemo(
    () => [
      columnHelper.accessor("route_name", {
        cell: (info) => info.getValue(),
        header: "Name",
      }),
      columnHelper.display({
        id: "knocked",
        header: "Knocked",
        cell(props) {
          const properties = props.row.original.properties ? JSON.parse(props.row.original.properties) : [];
          return (
            <RouteInput
              max={properties.length}
              value={props.row.original.knocked}
              apiCall={(val) => {
                console.log(val);
                debouncedApiCall({ ...props.row.original, knocked: val });
              }}
            />
          );
        },
      }),
      columnHelper.display({
        id: "contacted",
        header: "Contacted",
        cell(props) {
          const properties = props.row.original.properties ? JSON.parse(props.row.original.properties) : [];
          return (
            <RouteInput
              max={properties.length}
              value={props.row.original.contacted}
              apiCall={(val) => {
                console.log(val);
                debouncedApiCall({ ...props.row.original, contacted: val });
              }}
            />
          );
        },
      }),
      columnHelper.display({
        id: "appointment",
        header: "Appointments",
        cell(props) {
          const properties = props.row.original.properties ? JSON.parse(props.row.original.properties) : [];
          return (
            <RouteInput
              max={properties.length}
              value={props.row.original.appointments}
              apiCall={(val) => {
                console.log(val);
                debouncedApiCall({ ...props.row.original, appointments: val });
              }}
            />
          );
        },
      }),
      columnHelper.display({
        id: "status",
        header: "Status",
        cell(props) {
          return (
            <select
              className="border px-4 py-2 outline-none"
              id="knocked"
              onChange={(e) => {
                debouncedApiCall({
                  ...props.row.original,
                  status: e.target.value,
                });
              }}
              value={props.row.original.status}
            >
              <option className="px-4 py-2" value="Incomplete">
                Incomplete
              </option>
              <option className="px-4 py-2" value="Complete">
                Complete
              </option>
            </select>
          );
        },
      }),
      columnHelper.display({
        id: "action",
        cell(props) {
          // radix menu
          return (
            <MenuBar.Root>
              <MenuBar.Menu>
                <MenuBar.Trigger className="text-primary px-4 py-2">
                  <FontAwesomeIcon icon={faEllipsisV} />
                </MenuBar.Trigger>
                <MenuBar.Content
                  className="font-semibold bg-white border border-gray-200 shadow-lg rounded-md z-50"
                  sideOffset={5}
                  align="start"
                >
                  <MenuBar.Item
                    className="text-primary px-4 py-2 cursor-pointer"
                    onSelect={() => {
                      handleRouteView(props.row.original);
                      props.row.toggleExpanded();
                    }}
                  >
                    View
                  </MenuBar.Item>
                  <MenuBar.Item
                    className="text-green-600 px-4 py-2 cursor-pointer"
                    onSelect={() =>
                      PrintRouteDocs(props.row.original.public_id)
                    }
                    disabled={printLoading}
                  >
                    {printLoading &&
                    printingDoc === props.row.original.public_id ? (
                      <Spinner />
                    ) : (
                      "Print"
                    )}
                  </MenuBar.Item>
                  <MenuBar.Item
                    className="text-red-600 px-4 py-2 cursor-pointer"
                    onSelect={() => handleDelete(props.row.original.public_id)}
                  >
                    Delete
                  </MenuBar.Item>
                </MenuBar.Content>
              </MenuBar.Menu>
            </MenuBar.Root>
          );
        },
      }),
    ],
    [columnHelper]
  );

  const table = useReactTable({
    columns,
    data: userRoutes,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });
  return (
    <div className="w-full p-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">
          {isTeamRoutes ? "Team Routes" : "My Routes"}
        </h3>
        <button onClick={() => navigate("/")} className="h-10 w-10">
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      <div className="mt-6">
        <div className="mt-5">
          <Table heading={table.getHeaderGroups()} body={table.getRowModel()} />
        </div>
      </div>
      {printingDoc ? (
        <PdfModal
          handleClose={() => {
            if (controller.current) {
              controller.current.abort();
            }
            setPrintLoading(false);
            setPrintingDoc(null);
            setPdfUrl(null);
          }}
          loading={printLoading}
          pdfUrl={pdfUrl || ""}
        />
      ) : null}
    </div>
  );
};

const PdfModal = ({
  handleClose,
  loading,
  pdfUrl,
}: {
  handleClose: () => void;
  loading: boolean;
  pdfUrl: string;
}) => {
  return (
    <div>
      <Dialog.Root open={true}>
        <Dialog.Portal>
          <Dialog.Overlay className="bg-gray-800 bg-opacity-60 fixed inset-0 z-40" />
          <Dialog.Content
            style={{ transform: "translate(-50%, -50%)" }}
            className="fixed left-1/2 top-1/2 bg-white p-6 z-40 min-w-[30rem]"
          >
            <Dialog.Title className="text-gray-900 text-lg font-semibold">
              Print
            </Dialog.Title>

            <Dialog.Close>
              <button onClick={handleClose} className="absolute top-6 right-6">
                <CloseIcon />
              </button>
            </Dialog.Close>

            <div>
              {loading ? (
                <div className="w-full flex justify-center p-10">
                  <Spinner size={10} />
                </div>
              ) : null}
              {pdfUrl && !loading ? (
                <>
                  <iframe
                    title="pdf"
                    src={pdfUrl}
                    style={{ width: "60vw", height: "70vh" }}
                  ></iframe>
                  <div className="mt-6 flex w-full justify-end gap-x-4">
                    <button
                      onClick={handleClose}
                      className="px-4 py-2 border"
                      type="button"
                    >
                      Close
                    </button>
                    <a
                      className="px-4 py-2 bg-primary text-white hover:text-white"
                      download
                      href={pdfUrl}
                    >
                      Download
                    </a>
                  </div>
                </>
              ) : null}
              {!pdfUrl && !loading ? (
                <h3 className="p-4 text-lg font-semibold">
                  No Documents Found
                </h3>
              ) : null}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
};

export const RouteInput = ({
  max,
  value,
  apiCall,
}: {
  max: number;
  value: number;
  apiCall: (val: number) => void;
}) => {
  const [inputVal, setInputVal] = useState(value);

  useEffect(() => {
    if (value === inputVal || isNaN(inputVal) || inputVal < 0) {
      return;
    }
    apiCall(inputVal);
  }, [inputVal]);

  return (
    <input
      max={max}
      min={0}
      className="border px-4 py-2 outline-none w-20"
      type="number"
      id="knocked"
      value={inputVal}
      onChange={(e) => setInputVal(parseInt(e.target.value))}
    />
  );
};
